import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./index.css";
import ROUTES from "./constants/routes";
import { clearTokens } from "./utils/token";
import { clearAllCache } from "./api/client";

// Context & Wrappers
import AuthContext from "./AuthContext";
import { ToastProvider } from "./context/ToastContext";
import ProtectedWrapper from "./ProtectedWrapper";
import Layout from "./components/Layout";
import { SkeletonGrid } from "./components/SkeletonLoader";

// Lazy load pages for code splitting
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const Inventory = lazy(() => import("./pages/Inventory"));
const ItemDetail = lazy(() => import("./pages/ItemDetail"));
const Orders = lazy(() => import("./pages/Orders"));
const OrderDetail = lazy(() => import("./pages/OrderDetail"));
const Reviews = lazy(() => import("./pages/Reviews"));
const Returns = lazy(() => import("./pages/Returns"));
const QuoteRequests = lazy(() => import("./pages/QuoteRequests"));
const Users = lazy(() => import("./pages/Users"));
const CategoryManagement = lazy(() => import("./pages/CategoryManagement"));
const InstitutionManagement = lazy(() => import("./pages/InstitutionManagement"));
const CreateItem = lazy(() => import("./pages/CreateItem"));
const Store = lazy(() => import("./pages/Store"));
const Sales = lazy(() => import("./pages/Sales"));
const PurchaseDetails = lazy(() => import("./pages/PurchaseDetails"));
const SiteSettings = lazy(() => import("./pages/SiteSettings"));
const StoreAddresses = lazy(() => import("./pages/StoreAddresses"));
const NoPageFound = lazy(() => import("./pages/NoPageFound"));


// Loading fallback component
const PageLoader = () => (
  <div className="w-full space-y-4 sm:space-y-6 p-4 sm:p-6">
    <div className="h-8 bg-gray-200 rounded w-64 animate-pulse"></div>
    <SkeletonGrid count={6} />
  </div>
);

const App = () => {
  const [user, setUser] = useState("");
  const [loader, setLoader] = useState(true);
  const myLoginUser = JSON.parse(localStorage.getItem("user"));
  // console.log("USER: ",user)

  useEffect(() => {
    if (myLoginUser) {
      // Backend returns 'id' not '_id'
      const userId = myLoginUser.id || myLoginUser._id;
      setUser(userId);
      setLoader(false);
      // console.log("inside effect", myLoginUser)
    } else {
      setUser("");
      setLoader(false);
    }
  }, [myLoginUser]);

  const signin = (newUser, callback) => {
    setUser(newUser);
    callback();
  };

  const signout = () => {
    setUser(null);
    localStorage.removeItem("user");
    clearTokens();
    clearAllCache();
  };

  const value = { user, signin, signout };

  if (loader)
    return (
      <div
        style={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h1>LOADING...</h1>
      </div>
    );

  // Only use basename in production (when served from /admin/)
  // In development, app runs on its own port (3000) so no basename needed
  // Check if we're being served from /admin/ path
  // Use window.location.pathname to detect if we're under /admin/ route
  const getBasename = () => {
    const pathname = window.location.pathname;
    // Check if pathname starts with /admin (with or without trailing slash)
    if (pathname.startsWith('/admin')) {
      return '/admin';
    }
    return undefined;
  };
  const basename = getBasename();
  
  // Debug logging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('[App] Current pathname:', window.location.pathname);
    console.log('[App] Calculated basename:', basename);
  }

  return (
    <AuthContext.Provider value={value}>
      <ToastProvider>
        <BrowserRouter basename={basename}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
            {/* Public Routes */}
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />

            {/* Protected Routes */}
            <Route
              path={ROUTES.DASHBOARD}
              element={
                <ProtectedWrapper>
                  <Layout />
                </ProtectedWrapper>
              }
            >
              <Route index element={<Navigate to={ROUTES.INVENTORY} replace />} />
              <Route path="inventory" element={<Inventory />} />
              <Route path="inventory/create" element={<CreateItem />} />
              <Route path="inventory/items/:itemId" element={<ItemDetail />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:orderId" element={<OrderDetail />} />
              <Route path="catalog/categories" element={<CategoryManagement />} />
              <Route path="catalog/institutions" element={<InstitutionManagement />} />
              <Route path="purchase-details" element={<PurchaseDetails />} />
              <Route path="sales" element={<Sales />} />
              <Route path="manage-store" element={<Store />} />
              <Route path="site-settings" element={<SiteSettings />} />
              <Route path="store-addresses" element={<StoreAddresses />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="returns" element={<Returns />} />
              <Route path="quote-requests" element={<QuoteRequests />} />
              <Route path="users" element={<Users />} />
            </Route>
            <Route path="*" element={<NoPageFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ToastProvider>
    </AuthContext.Provider>
  );
};

export default App;
